import type { ExploratoryEffort, FuzzReportJson, ReviewFlavor } from '@chatsdet/types';

export type ExploratoryTestStatus =
  | 'pending'
  | 'planning'
  | 'running'
  | 'completed'
  | 'failed'
  | 'canceled';

export type SpecOrchestrationBlocker = {
  code: string;
  ref: string;
  stepHint: string | null;
  message: string;
  candidates?: string[];
};
export type SpecOrchestrationState = {
  actors: Array<{
    label: string;
    ref: string;
    credentialId: string;
    credentialName: string;
    role: string | null;
    selectionRationale: string;
    matchConfidence: 'high' | 'medium' | 'low';
  }>;
  seeds: Array<{
    ref: string;
    workflowId: string;
    workflowName: string;
    selectionRationale: string;
    matchConfidence: 'high' | 'medium' | 'low';
    sequence: number;
    status: 'selected' | 'running' | 'completed' | 'failed';
  }>;
  blockers: SpecOrchestrationBlocker[];
  initializedAt: string;
};
export type SpecLedgerItem = {
  id: string;
  statement: string;
  status: 'pending' | 'in_progress' | 'verified' | 'failed' | 'blocked' | 'skipped';
  actorLabel: string | null;
  note: string | null;
  evidence: string | null;
  startedAtMs: number | null;
  completedAtMs: number | null;
};

export type ExploratoryTestListItem = {
  id: string;
  orgId: string;
  propertyId: string;
  propertyName: string;
  credentialId: string | null;
  /** Durable parent (exploratory_tests) this run belongs to; null for PR/ticket/legacy runs. */
  exploratoryTestId: string | null;
  title: string | null;
  instructions: string;
  startUrl: string | null;
  status: ExploratoryTestStatus;
  issuesFound: number;
  /** Review flavor. Absent on older rows → treat as 'functional'. */
  flavor?: ReviewFlavor;
  /** Time budget chosen at start. Null for flavors without an effort (everything but the fuzzer). */
  effort?: ExploratoryEffort | null;
  /** Convenience copy of the UX overall score (0–100). Null for functional runs. */
  overallScore?: number | null;
  summary: string | null;
  videoStorageKey: string | null;
  /** Playwright trace archive key. Present when trace capture ran (default-on).
   *  Fetch/view via `GET /adhoc-tests/:id/trace`. Null for older runs. */
  traceStorageKey: string | null;
  videoTimelineJson: import('@chatsdet/types').VideoTimeline | null;
  reportJson: ExploratoryTestReportJson | null;
  createdByEmail: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  error: string | null;
  createdAt: string;
  /** Plan authored by the on-sandbox planner agent before the tester ran.
   *  Null for legacy / non-planning runs. */
  plannedTestPlan?: ExploratoryTestPlanItem[] | null;
  /** Behaviors the planner could not verify in the sandbox, with how a human
   *  would test them elsewhere. Null for legacy / non-planning runs. */
  plannedUntestable?: ExploratoryUntestableItem[] | null;
  plannedDataNotes?: string | null;
  /** At-a-glance "what this plan tests" line authored by the planner. Null on
   *  legacy / non-planning runs or runs planned before the field existed. */
  plannedSummary?: string | null;
  /** Set when the row enters the planning state. */
  planningStartedAt?: string | null;
  plannedAt?: string | null;
  coverageWarning?: string | null;
  externalTicketProvider?: 'jira' | 'linear' | 'azureDevops' | 'github' | null;
  externalTicketIdentifier?: string | null;
  externalTicketUrl?: string | null;
  /** GitHub PR linkage, set when the run was triggered by a PR mention. */
  githubRepoFullName: string | null;
  githubPrNumber: number | null;
  githubPrTitle: string | null;
  targetKind?: 'live' | 'sandbox' | 'sandbox-v2';
  sourceAdhocTestId?: string | null;
  sandboxV2TestedSha?: string | null;
  /** Working checklist persisted per status change while a planned sandbox
   *  test runs. Detail responses only; null/absent for self-planning runs and
   *  list rows. Once `reportJson` lands, it wins. */
  liveTestPlan?: ExploratoryTestLiveChecklistItem[] | null;
  specPreflightJson?: SpecOrchestrationState | null;
  specLedgerJson?: SpecLedgerItem[] | null;
  /** Set once a spec-review run has been auto-promoted to a draft workflow
   *  by Flow Autobuild. Null until then. Row passthrough — present on both
   *  list and detail responses. */
  promotedWorkflowId: string | null;
  promotedAt: string | null;
};

/**
 * Skinny per-run summary for the run-history affordance: the status, timing,
 * and the report (used to derive the pass/fail verdict client-side). Returned
 * by `GET /exploratory-tests/:parentId/runs`, oldest first.
 */
export type ExploratoryTestRunSummary = {
  id: string;
  status: ExploratoryTestStatus;
  reportJson: ExploratoryTestReportJson | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

export type ExploratoryTestRunsResponse = {
  ok: boolean;
  runs: ExploratoryTestRunSummary[];
};

/**
 * A recurring schedule for an exploratory test, backed by a schedule-triggered
 * `exploratory-test.run` automation rule. `description` is the concise English
 * schedule (the display source of truth).
 */
export type ExploratoryTestSchedule = {
  ruleId: string;
  description: string;
  cron: string | null;
  timezone: string | null;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunStatus: string | null;
  exploratoryTestId: string | null;
  testName: string | null;
  propertyName: string | null;
};

export type ExploratoryTestSchedulesResponse = {
  ok: boolean;
  schedules: ExploratoryTestSchedule[];
};

export type ExploratoryTestPlanItemStatus = 'passed' | 'failed' | 'skipped' | 'question';

export type ExploratoryTestPlanItem = {
  name: string;
  status: ExploratoryTestPlanItemStatus;
  details?: string;
  /** Case asserts correct rejection; the tester scores rejection as passed. */
  negative?: boolean;
  /** Expected behavior was unknowable up front; the tester reports `question`. */
  ambiguous?: boolean;
};

export type ExploratoryTestLiveChecklistStatus =
  | 'pending'
  | 'in_progress'
  | ExploratoryTestPlanItemStatus;

/** One live-checklist item — mirrors LiveChecklistItem in the API's adhocFinalReportTool.ts. */
export type ExploratoryTestLiveChecklistItem = {
  /** Stable `TODO-n` id assigned in plan order. */
  id: string;
  name: string;
  status: ExploratoryTestLiveChecklistStatus;
  details?: string | null;
  priority?: 'p0' | 'p1' | 'p2' | 'p3';
  negative?: boolean;
  ambiguous?: boolean;
  /** Found during execution, not in the authored plan. */
  discovered?: boolean;
  /** Scenario (`SCENARIO-n`) this item runs under, for multi-user runs. */
  scenarioId?: string | null;
  startedAtMs?: number | null;
  completedAtMs?: number | null;
};

export type ExploratoryUntestableReason =
  | 'external_integration'
  | 'gated_not_enableable'
  | 'needs_real_environment'
  | 'manual_only'
  | 'other';

export type ExploratoryUntestableItem = {
  name: string;
  reason: ExploratoryUntestableReason;
  howToTest: string;
};

/** Coarse bucket for a UX-oddity note — mirrors ObservationKind in the API's observationTool.ts. */
export type ExploratoryTestObservationKind = 'ux_oddity' | 'confusion' | 'rough_edge';

/**
 * A non-defect note the tester recorded via `note_observation`: a UX oddity,
 * point of confusion, or rough edge. Never gates the verdict; surfaced
 * first-class in the Report tab. Mirrors Observation in the API's
 * observationTool.ts.
 */
export type ExploratoryTestObservation = {
  kind: ExploratoryTestObservationKind;
  title: string;
  note: string;
};

export type ExploratoryTestReportJson = {
  status: string;
  summary: string;
  testPlan: ExploratoryTestPlanItem[];
  /**
   * Cases the tester discovered during execution that were NOT in the
   * authored plan. Only populated when running in execute-plan mode.
   */
  appended?: ExploratoryTestPlanItem[];
  /**
   * UX oddities / confusions / rough edges the tester noted during the run.
   * Not defects, not gated into the verdict. Absent on older runs.
   */
  observations?: ExploratoryTestObservation[];
};

export type ExploratoryTestAction = {
  id: string;
  actionType: string;
  actionIndex: number | null;
  elementDescription: string | null;
  pageUrl: string | null;
  success: boolean | null;
  errorMessage: string | null;
  timestamp: string;
};

/**
 * Set on a rejected candidate verification when the rejection matched a
 * product-map capability Rule (the observed behavior is expected per that
 * rule, not a bug).
 */
export type ExpectedRule = {
  ruleId: string;
  statement: string;
  capabilityId: string;
  capabilityCode: string | null;
  capabilityTitle: string;
};

export type ExploratoryTestCandidateVerification = {
  id: string;
  candidateDescription: string;
  candidateSeverity: string;
  candidateReproSteps: string;
  candidateObservedBehavior: string;
  candidateExpectedBehavior: string;
  candidateScreenshotLabel: string | null;
  verificationStatus: string;
  filedIssueId: string | null;
  verifiedTitle: string | null;
  rejectionReason: string | null;
  expectedRule: ExpectedRule | null;
  pageUrl: string | null;
  createdAt: string;
};

export type ExploratoryTestsResponse = {
  ok: boolean;
  items: ExploratoryTestListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export type ExploratoryTestEnvironmentInstance = {
  instanceId: string;
  publicUrl: string | null;
  seededCredential: {
    id: string;
    name: string;
    role: string | null;
  } | null;
};

export type ExploratoryTestDetailResponse = {
  ok: boolean;
  data: ExploratoryTestListItem;
  actions: ExploratoryTestAction[];
  candidateVerifications: ExploratoryTestCandidateVerification[];
  environmentInstance: ExploratoryTestEnvironmentInstance | null;
  /** Graded-review findings (UX flavor). Empty/absent for functional runs. */
  reviewFindings?: ReviewFinding[];
  browserSessions: ExploratoryBrowserSession[];
};

export type ExploratoryBrowserSession = {
  id: string;
  adhocTestId: string;
  sandboxV2SandboxId: string | null;
  browserSessionId: string;
  persona: string | null;
  status: 'allocating' | 'active' | 'closed' | 'failed';
  startedAt: string | null;
  finishedAt: string | null;
  videoStorageKey: string | null;
  traceStorageKey: string | null;
  videoTimelineJson: import('@chatsdet/types').VideoTimeline | null;
  error: string | null;
};

// ── UX-review flavor shapes ──────────────────────────────────────────────────

export type ReviewSeverity = 'critical' | 'high' | 'medium' | 'low';
export type ReviewFeedbackState = 'unreviewed' | 'confirmed' | 'dismissed_false_positive';

/** A single UX review finding (persisted in `review_findings`). */
export type ReviewFinding = {
  id: string;
  severity: ReviewSeverity;
  dimension: string;
  title: string;
  recommendation: string | null;
  detail: string | null;
  measuredOrJudgment: 'measured' | 'judgment';
  primaryUrl: string | null;
  reproSteps: string | null;
  observedBehavior: string | null;
  expectedBehavior: string | null;
  screenshotStorageKey: string | null;
  videoTimestampMs: number | null;
  /** Session row whose video holds `videoTimestampMs`; null on pre-multi-session findings (first session). */
  browserSessionId: string | null;
  annotationRef: string | null;
  consoleErrorSeen: boolean;
  networkFailureSeen: boolean;
  promotedIssueId: string | null;
  feedbackState: ReviewFeedbackState;
  feedbackNote: string | null;
};

/** The `reportJson` shape for a UX run (mirrors api ReviewReportJson). */
export type UxReviewReportJson = {
  flavor: string;
  goalOutcome: string;
  summary: string;
  overallScore: number;
  overallGrade: string;
  dimensions: Array<{
    key: string;
    label: string;
    score: number;
    grade: string;
    note?: string;
  }>;
  promptVersion: number;
  findingCount: number;
  device: string;
};

// ── Spec-review flavor shapes ─────────────────────────────────────────────────

/** Mirrors `SpecReportInput['specItems'][number]` in apps/api/src/ai/tools/qa/specReportTool.ts. */
export type SpecReviewSpecItem = {
  statement: string;
  testable: 'yes' | 'partial' | 'no';
  reason: string;
  evidence?: string;
};

/** Mirrors `SpecReportInput['assumptions'][number]`. */
export type SpecReviewAssumption = {
  assumption: string;
  status: 'verified' | 'failed' | 'unverifiable';
  evidence: string;
};

/** Mirrors `SpecReportInput['dataDependencies'][number]`. */
export type SpecReviewDataDependency = {
  description: string;
  provenance: 'pre_existing' | 'created_by_flow' | 'external_system' | 'unknown';
  canSeedInTest: boolean;
  notes?: string;
};

/**
 * The `reportJson` shape for a spec-review run. Mirrors `SpecReviewReportJson`
 * in apps/api/src/adhoc-test/spec-report.ts. Unlike `UxReviewReportJson`,
 * there is no score/grade — spec review is intentionally ungraded.
 */
export type SpecReviewReportJson = {
  flavor: 'spec';
  summary: string;
  specItems: SpecReviewSpecItem[];
  assumptions: SpecReviewAssumption[];
  idempotency: {
    verdict: 'idempotent' | 'not_idempotent' | 'conditional';
    notes: string;
  };
  dataDependencies: SpecReviewDataDependency[];
  flowReadiness: {
    convertible: 'yes' | 'with_changes' | 'no';
    blockers: string[];
  };
  cleanedSpec: string;
  counts: { testable: number; partial: number; notTestable: number };
  promptVersion: number;
  findingCount: number;
  device: string;
};

export type StartExploratoryTestResponse = {
  ok: boolean;
  adhocTestId: string;
  jobId: string;
};

export type ExploratoryTestBadgeVariant =
  | 'success'
  | 'secondary'
  | 'warning'
  | 'destructive'
  | 'outline';

export function getExploratoryTestStatusMeta(status: ExploratoryTestStatus): {
  label: string;
  color: 'green' | 'blue' | 'yellow' | 'red' | 'gray';
  badgeVariant: ExploratoryTestBadgeVariant;
  statusName: import('@chatsdet/ui').StatusName;
} {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: 'gray', badgeVariant: 'outline', statusName: 'queued' };
    case 'planning':
      return { label: 'Planning', color: 'blue', badgeVariant: 'secondary', statusName: 'running' };
    case 'running':
      return { label: 'Running', color: 'blue', badgeVariant: 'secondary', statusName: 'running' };
    case 'completed':
      return { label: 'Completed', color: 'green', badgeVariant: 'success', statusName: 'success' };
    case 'failed':
      return { label: 'Failed', color: 'red', badgeVariant: 'destructive', statusName: 'failed' };
    case 'canceled':
      return {
        label: 'Canceled',
        color: 'yellow',
        badgeVariant: 'warning',
        statusName: 'canceled',
      };
    default:
      return { label: 'Unknown', color: 'gray', badgeVariant: 'outline', statusName: 'queued' };
  }
}

/** The `reportJson` shape for a fuzz run (see `FuzzReportJson`). */
export type { FuzzReportJson };
